import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Newspaper, Loader2, ShieldQuestion, ExternalLink, Check, X, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const severityTone = (band) => ({
  high: 'bg-rose-600 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-blue-500 text-white',
  review_only: 'bg-slate-500 text-white',
  clear: 'bg-emerald-500 text-white',
}[band] || 'bg-slate-400 text-white');

const fmtTime = (iso) => {
  if (!iso) return 'unknown time';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

const relTime = (seconds) => {
  if (seconds == null) return 'just now';
  if (seconds < 60) return 'moments ago';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minute(s) ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour(s) ago`;
  return `${Math.floor(seconds / 86400)} day(s) ago`;
};

const identityTone = (band) => ({
  high: 'border-rose-300 text-rose-700',
  medium: 'border-amber-300 text-amber-700',
  low: 'border-slate-300 text-slate-600',
}[band] || 'border-slate-300 text-slate-600');

export default function AdverseMediaPanel({ subjectName, scanId, canReview }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Discovery runs in a background queue, so the panel reads the subject's
  // state instead of holding a request open. It polls only while a job is in
  // flight, and stops as soon as the queue settles.
  const loadState = useCallback(async () => {
    if (!subjectName) return null;
    try {
      const { data } = await axios.get(`${API}/adverse-media/state`, {
        params: { name: subjectName },
        headers: authHeaders(),
      });
      setState(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not read adverse-media state');
      return null;
    }
  }, [subjectName]);

  useEffect(() => {
    loadState();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadState]);

  useEffect(() => {
    if (!state?.refreshing) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const next = await loadState();
      if (next && !next.refreshing && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  }, [state?.refreshing, loadState]);

  const runSearch = async (force) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/adverse-media/${force ? 'refresh' : 'search'}`,
        { name: subjectName, scan_id: scanId },
        { headers: authHeaders(), timeout: 30000 }
      );
      setState(data);
      toast.success(data.queued
        ? 'Adverse-media discovery queued — results will appear here'
        : `${data.findings?.length || 0} adverse-media finding(s) — all unvalidated`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not queue adverse-media discovery';
      setError(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const review = async (findingId, factId, decision) => {
    const reason = window.prompt(`Reason for ${decision === 'validate' ? 'validating' : 'rejecting'} this fact:`);
    if (!reason) return;
    try {
      await axios.post(
        `${API}/adverse-media/findings/${findingId}/facts/${factId}/review`,
        { decision, reason },
        { headers: authHeaders() }
      );
      toast.success(`Fact ${decision === 'validate' ? 'validated' : 'rejected'}`);
      setState((prev) => (!prev ? prev : {
        ...prev,
        findings: prev.findings.map((f) =>
          f.id !== findingId ? f : {
            ...f,
            facts: f.facts.map((x) => (x.fact_id === factId
              ? { ...x, status: decision === 'validate' ? 'validated' : 'rejected' } : x)),
          }),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    }
  };

  return (
    <Card className="border border-slate-200" data-testid="adverse-media-panel">
      <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="text-base font-semibold text-slate-900">Adverse Media</h3>
            <p className="text-xs text-slate-500">
              Separate screening domain — never merged into sanctions evidence, never alters a sanctions score
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => runSearch(false)}
            disabled={busy || state?.refreshing}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="run-adverse-media-btn"
          >
            {busy || state?.refreshing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {busy ? 'Queueing…' : 'Queued — searching'}
              </span>
            ) : 'Search negative news'}
          </Button>
          {(state?.state === 'COMPLETE' || state?.state === 'PARTIAL' || state?.state === 'FAILED') && (
            <Button
              variant="outline"
              onClick={() => runSearch(true)}
              disabled={busy || state?.refreshing}
              data-testid="refresh-adverse-media-btn"
            >
              Force refresh
            </Button>
          )}
        </div>
      </div>

      {/* The panel always states where it stands: current, historical with a
          refresh in flight, queued, or a stated failure. A failure is never
          shown as "no adverse media found". */}
      <div className="px-5 pt-4 text-xs" data-testid="adverse-media-status">
        {state?.refreshing && !state?.result && (
          <span className="text-slate-500" data-testid="adverse-media-queued">
            Adverse-media discovery queued — current results pending
            {state?.attempts > 1 ? ` (attempt ${state.attempts})` : ''}.
          </span>
        )}
        {state?.refreshing && state?.result && (
          <span className="text-amber-800" data-testid="adverse-media-refreshing">
            Refreshing adverse-media discovery… showing the last successful result below.
          </span>
        )}
        {!state?.refreshing && state?.state === 'FAILED' && (
          <span className="text-amber-900 font-medium" data-testid="adverse-media-failed">
            Discovery failed — {state.attempts} attempt(s), last at{' '}
            {fmtTime(state.last_attempt_at)}. This is not a clean result.
          </span>
        )}
        {!state?.refreshing && (state?.state === 'COMPLETE' || state?.state === 'PARTIAL') && (
          <span className={state.historical ? 'text-amber-800' : 'text-slate-500'}>
            {state.historical
              ? `Historical — as of ${fmtTime(state.searched_at)}`
              : `Current — searched ${relTime(state.result_age_seconds)}`}
            {state.state === 'PARTIAL' ? ' · partial' : ''}
            {state.result?.discovery_source ? ` · source: ${state.result.discovery_source}` : ''}
          </span>
        )}
        {(!state || state.state === 'NONE') && !state?.refreshing && (
          <span className="text-slate-500" data-testid="adverse-media-none">
            No adverse-media search has run for this subject yet.
          </span>
        )}
      </div>

      {error && (
        <div
          className="mx-5 mt-4 mb-1 rounded border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="adverse-media-error"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <p className="font-medium">Discovery unavailable — this is not a clean result.</p>
            <p className="mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {state?.state === 'FAILED' && state?.last_error && (
        <div
          className="mx-5 mt-4 rounded border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="adverse-media-failure-detail"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <p className="font-medium">Could not search — this is not "nothing found".</p>
            <p className="mt-0.5">{state.last_error}</p>
            {state.providers_tried?.length > 0 && (
              <p className="mt-0.5 text-amber-800">
                Tried: {state.providers_tried.map((p) => `${p.provider} (${p.status})`).join(', ')}
              </p>
            )}
          </div>
        </div>
      )}

      {state?.result && (
        <div className="p-5 space-y-4">
          {(state.historical || state.state === 'PARTIAL') && (
            <div
              className="rounded border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
              data-testid="adverse-media-stale-notice"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <p className="font-medium">
                  {state.historical
                    ? `Last successful result — ${fmtTime(state.searched_at)}`
                    : 'Partial result — not everything completed'}
                </p>
                <p className="mt-0.5">
                  {state.result.notice
                    || 'Treat this as historical: it is not a current search. Use Force refresh for a fresh one.'}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <Badge className={severityTone(state.result.highest_severity_band)} data-testid="adverse-media-severity">
              Event severity: {String(state.result.highest_severity_band).replace('_', ' ')}
            </Badge>
            <span>{state.result.articles_found} article(s)</span>
            <span className="text-slate-400">·</span>
            <span>{state.result.independent_publishers} independent publisher(s)</span>
            <span className="text-slate-400">·</span>
            <span>discovery: {state.result.discovery_source || 'GDELT'}</span>
          </div>

          {state.findings.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="adverse-media-empty">
              Searched, and no negative news was found for this subject in the discovery index.
            </p>
          )}

          {state.findings.map((finding, idx) => (
            <div
              key={finding.id}
              className="p-4 border border-slate-200 rounded space-y-2"
              data-testid={`adverse-media-finding-${idx}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <p className="font-medium text-slate-900 text-sm flex-1">{finding.headline}</p>
                <div className="flex items-center gap-1.5">
                  <Badge className={severityTone(finding.severity_band)}>
                    {String(finding.severity_band).replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline" className={identityTone(finding.identity_band)}>
                    identity {finding.identity_band} ({Math.round((finding.identity_confidence || 0) * 100)}%)
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                <span className="font-medium text-slate-700">{finding.publisher}</span>
                <span>{finding.source_reliability}</span>
                <span>{finding.source_class?.replace(/_/g, ' ')}</span>
                {finding.published_date && <span>{String(finding.published_date).slice(0, 8)}</span>}
                {finding.language && <span>{finding.language}</span>}
                <span>{finding.corroboration_status?.replace('_', ' ')}</span>
                <span>stage: {finding.event_stage}</span>
                {finding.url && (
                  <a href={finding.url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center text-blue-600 hover:underline">
                    <ExternalLink className="w-3 h-3 mr-1" />source
                  </a>
                )}
              </div>

              {(finding.facts || []).length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-slate-100">
                  {finding.facts.map((fact) => (
                    <div key={fact.fact_id} className="flex items-start justify-between gap-2">
                      <div className="text-xs text-slate-700">
                        <span className="font-mono text-[10px] uppercase text-slate-400 mr-1.5">
                          {fact.fact_type?.replace(/_/g, ' ')}
                        </span>
                        {fact.value}
                        {fact.supporting_quote && (
                          <span className="block text-[11px] text-slate-500 italic">“{fact.supporting_quote}”</span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] ${
                            fact.status === 'validated' ? 'bg-emerald-100 text-emerald-700'
                              : fact.status === 'rejected' ? 'bg-slate-200 text-slate-600'
                                : 'bg-amber-100 text-amber-800'}`}
                        >
                          <ShieldQuestion className="w-3 h-3" />
                          {fact.status === 'pending_review' ? 'AI-Extracted / Unvalidated' : fact.status}
                        </span>
                      </div>
                      {canReview && fact.status === 'pending_review' && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" className="h-7 px-2"
                                  onClick={() => review(finding.id, fact.fact_id, 'validate')}
                                  data-testid={`validate-fact-${fact.fact_id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2"
                                  onClick={() => review(finding.id, fact.fact_id, 'reject')}
                                  data-testid={`reject-fact-${fact.fact_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <p className="text-[11px] text-slate-500">{state.notice}</p>
        </div>
      )}
    </Card>
  );
}
